<template>
<div class="d-flex">
	<!-- Sidebar menu (hide if < lg) -->
	<div
		class="d-none d-lg-flex flex-column sticky-top top-1 bg-body-tertiary border-radius"
		:class="{
			'p-0 p-1 pe-3 m-0 mt-2 ms-2 me-1 me-xl-2': isDesktop,
			'': isMobile,
		}"
		style="height: min-content; width: auto;"
	>
		<div class="p-1">
			<a class="nav-link text-nowrap align-text-bottom" href="#common_settings">
				<unit-icon />
				Загальні налаштування
			</a>
		</div>

		<div class="p-1">
			<a class="nav-link text-nowrap align-text-bottom d-flex" href="#organization" @click="spoilersShow['organization'] = true">
				<menu-icon name="organization" color="currentColor" edgeSize="16" class="pe-2 me-1" />
				Експлуатуюча організація
			</a>
		</div>
		<div class="p-1">
			<a class="nav-link text-nowrap align-text-bottom" href="#location" @click="spoilersShow['location'] = true">
				<unit-icon />
				Місце розташування
			</a>
		</div>
		<div class="p-1">
			<a class="nav-link text-nowrap align-text-bottom" href="#technical_data" @click="spoilersShow['technical_data'] = true">
				<unit-icon />
				Технічні відомості УКЗ
			</a>
		</div>
		<div class="p-1">
			<a class="nav-link text-nowrap align-text-bottom" href="#electric_meter" @click="spoilersShow['electric_meter'] = true">
				<unit-icon unitType="power_meter" edgeSize="20" class="me-1" />
				{{ Station.unitNames[Station.passportKeys.electricMeter.self] }}
			</a>
		</div>
		<div class="p-1">
			<a class="nav-link text-nowrap align-text-bottom" href="#building_passport" @click="spoilersShow['building_passport'] = true">
				<unit-icon />
				Будівельний паспорт
			</a>
		</div>
		<div class="p-1">
			<a class="nav-link text-nowrap align-text-bottom" href="#related_stations" @click="spoilersShow['related_stations'] = true">
				<unit-icon />
				{{ Station.settingNames[Station.passportKeys.relatedStations] }}
			</a>
		</div>
	</div>

<div
	class="flex-grow-1 p-0 pt-0 m-0"
	:class="{'pe-1': isDesktop}"
>
	<Form
		class="m-0 p-0"
		@submit="onSubmit"
		@invalid-submit="console.log"
		:validation-schema="schema"
		:initial-values="formValues"
		v-slot="{ errors, meta, values, setFieldValue }"
		ref="passportForm"
		id="passport-form"
	>
	<fieldset :disabled="!isFormEnabled">
		<a id="common_settings" />
		<drop-down-spoiler v-model:show="spoilersShow['common_settings']" :sticky-title="true">
			<template v-slot:title>
				<h6 class="fs-5 fw-bold m-0">Загальні налаштування</h6>
			</template>
			<template v-slot:body>
				<div :class="{
					'px-3': isDesktop,
					'px-1': isMobile,
				}">
					<div class="row form-row">
						<div class="form-group col-auto input-min-width">
							<label>
								{{ Station.settingNames[Station.passportKeys.name] }}
							</label>
							<Field
								:name="Station.passportKeys.name"
								type="text"
								class="form-control form-control-sm"
								:class="{ 'is-invalid': errors[Station.passportKeys.name] }"
							/>
							<div class="invalid-feedback">{{ errors[Station.passportKeys.name] }}</div>
						</div>
					</div>
					<div class="row form-row">
						<div class="form-group col-auto input-min-width">
							<label>
								{{ Station.settingNames[Station.passportKeys.shortName] }}
							</label>
							<Field
								:name="Station.passportKeys.shortName"
								type="text"
								class="form-control form-control-sm"
								:class="{ 'is-invalid': errors[Station.passportKeys.shortName] }"
							/>
						<div class="invalid-feedback">{{ errors[Station.passportKeys.shortName] }}</div>
						</div>
					</div>
					<div class="row form-row">
						<div class="form-group col-auto my-1 input-min-width">
							<label>
								{{ Station.settingNames[Station.passportKeys.techId] }}
							</label>
							<Field
								:name="Station.passportKeys.techId"
								type="text"
								class="form-control form-control-sm"
								:class="{ 'is-invalid': errors[Station.passportKeys.techId] }"
							/>
							<div class="invalid-feedback">{{errors[Station.passportKeys.techId]}}</div>
						</div>
					</div>
					
					<div class="row form-row mb-3">
						<div class="form-group col-auto my-1 input-min-width">
							<label>{{ Station.settingNames[Station.passportKeys.pipelineId] }}</label>
							<Field
								:name="Station.passportKeys.pipelineId"
								as="select"
								class="form-control form-control-sm"
								:class="{ 'is-invalid': errors[Station.passportKeys.pipelineId] }"
								ref="pipeline_id_field"
							>
								<option value="" disabled selected>Натисніть щоб обрати</option>
								<option
									v-for="pip in pipelines()"
									:key="pip.id"
									:value="pip.id"
								>
									{{ pip.title }}
								</option>
							</Field>
							<div class="invalid-feedback">{{errors[Station.passportKeys.pipelineId]}}</div>
						</div>
					</div>
				</div>
			</template>
		</drop-down-spoiler>

		<!-- Organization -->
		<a id="organization" />
		<drop-down-spoiler v-model:show="spoilersShow['organization']" :sticky-title="true">
			<template v-slot:title>
				<div class="row row-cols-3 justify-content-between">
					<div class="col-auto text-nowrap d-flex">
						<span class="d-flex m-0 me-1 py-auto">
							<menu-icon name="organization" color="currentColor" edgeSize="20"/>&nbsp;
						</span>
						<h6 class="fs-5 fw-bold text-nowrap m-0">Експлуатуюча організація</h6>
					</div>
				</div>
			</template>
			<template v-slot:body>
				<div :class="{
					'px-3': isDesktop,
					'px-1': isMobile,
				}">
					<div class="row form-row">
						<div class="form-group col-auto my-1 input-min-width">
							<label>
								{{ Station.settingNames[Station.passportKeys.orgId] }}
							</label>
							<Field
								:name="Station.passportKeys.orgId"
								as="select"
								class="form-control form-control-sm"
								:class="{ 'is-invalid': errors[Station.passportKeys.orgId] }"
								:disabled="accountStore.organization.role == 'secondary'"
								@change="
									setFieldValue(Station.passportKeys.departmentId, null);
									setFieldValue(Station.passportKeys.sectorId, null);
									setFieldValue(Station.passportKeys.pipelineId, null);
									this.$refs.department_id_field.$el.disabled = true
									this.$refs.sector_id_field.$el.disabled = true
									this.$refs.pipeline_id_field.$el.disabled = true
								"
							>
								<option value="" disabled>Натисніть щоб обрати</option>
								<option
									v-for="org in organizations"
									:key="org._id"
									:value="org._id"
								>
									{{ org.title }}
								</option>
							</Field>
							<div class="invalid-feedback">{{errors[Station.passportKeys.orgId]}}</div>
						</div>
					</div>
					<div class="row form-row">
						<div class="form-group col-auto my-1 input-min-width">
							<label>{{ departmentTitle }}</label>
							<Field
								:name="Station.passportKeys.departmentId"
								as="select"
								class="form-control form-control-sm"
								:class="{ 'is-invalid': errors[Station.passportKeys.departmentId] }"
								ref="department_id_field"
								@change="setFieldValue('sector_id', null)"
							>
								<option value="" disabled selected>Натисніть щоб обрати</option>
								<option
									v-for="dep in departments()"
									:key="dep.id"
									:value="dep.id"
								>
									{{ dep.title }}
								</option>
							</Field>
							<div class="invalid-feedback">{{errors[Station.passportKeys.departmentId]}}</div>
						</div>
					</div>
					<div class="row form-row">
						<div class="form-group col-auto my-1 input-min-width">
							<label>{{ sectorTitle }}</label>
							<Field
								:name="Station.passportKeys.sectorId"
								as="select"
								class="form-control form-control-sm"
								:class="{ 'is-invalid': errors[Station.passportKeys.sectorId] }"
								ref="sector_id_field"
							>
								<option value="" disabled selected>Натисніть щоб обрати</option>
								<option
									v-for="sec in departmnetSectors(values[Station.passportKeys.departmentId])"
									:key="sec.id"
									:value="sec.id"
								>
									{{ sec.title }}
								</option>
							</Field>
							<div class="invalid-feedback">{{errors[Station.passportKeys.sectorId]}}</div>
						</div>
					</div>
				</div>
			</template>
		</drop-down-spoiler>

		<!-- Lacation -->
		<a id="location" />
		<drop-down-spoiler v-model:show="spoilersShow['location']" :sticky-title="true">
			<template v-slot:title>
				<h6 class="fs-5 fw-bold m-0">Місце розташування</h6>
			</template>
			<template v-slot:body>
				<div :class="{
					'px-3': isDesktop,
					'px-1': isMobile,
				}">
					<div class="row form-row">
						<div class="form-group col-auto my-1 input-min-width">
							<label>
								{{ Station.settingNames[Station.passportKeys.district] }}
							</label>
							<Field
								:name="Station.passportKeys.district"
								type="text"
								class="form-control form-control-sm"
								:class="{ 'is-invalid': errors[Station.passportKeys.district] }"
							/>
							<div class="invalid-feedback">{{ errors[Station.passportKeys.district] }}</div>
						</div>
					</div>
					<div class="row form-row">
						<div class="form-group col-auto my-1 input-min-width">
							<label>
								{{ Station.settingNames[Station.passportKeys.region] }}
							</label>
							<Field
								:name="Station.passportKeys.region"
								type="text"
								class="form-control form-control-sm"
								:class="{ 'is-invalid': errors[Station.passportKeys.region] }"
							/>
							<div class="invalid-feedback">{{errors[Station.passportKeys.region]}}</div>
						</div>
					</div>
					<div class="row form-row">
						<div
							class="form-group col-auto my-1 input-min-width"
							:style="{ width: meta.initialValues.post_address ? meta.initialValues.post_address.length+'ch' : '15rem' }"
						>
							<label>
								{{ Station.settingNames[Station.passportKeys.postAddress] }}
							</label>
							<Field
								:name="Station.passportKeys.postAddress"
								type="text"
								class="form-control form-control-sm"
								:class="{ 'is-invalid': errors[Station.passportKeys.postAddress] }"
							/>
							<div class="invalid-feedback">{{ errors[Station.passportKeys.postAddress] }}</div>
						</div>
					</div>
					<div class="row form-row">
						<div class="form-group col-auto my-1 input-min-width">
							<label>
								{{ Station.settingNames[Station.passportKeys.location] }}
							</label>
							<Field
								:name="`${Station.passportKeys.location}.type`"
								type="text"
								class="form-control form-control-sm"
								:disabled="true"
								:hidden="true"
							/>
							<Field
								:name="`${Station.passportKeys.location}.coordinates`"
								type="text"
								class="form-control form-control-sm"
								:class="{ 'is-invalid': errors[Station.passportKeys.location] }"
								:disabled="true"
							/>
							<div class="invalid-feedback">{{errors[Station.passportKeys.location] }}</div>
						</div>
					</div>
					<div class="row form-row">
						<div class="form-group col-auto my-1 input-min-width">
							<label>
								{{ Station.settingNames[Station.passportKeys.settlement] }}
							</label>
							<Field
								:name="Station.passportKeys.settlement"
								type="text"
								class="form-control form-control-sm"
								:class="{ 'is-invalid': errors[Station.passportKeys.settlement] }"
							/>
							<div class="invalid-feedback">{{errors[Station.passportKeys.settlement]}}</div>
						</div>
					</div>
					<div class="row form-row">
						<div class="form-group col-auto my-1 input-min-width">
							<label>
								{{ Station.settingNames[Station.passportKeys.streetHouse] }}
							</label>
							<Field
								name="street_house"
								type="text"
								class="form-control form-control-sm"
								:class="{ 'is-invalid': errors[Station.passportKeys.streetHouse] }"
							/>
							<div class="invalid-feedback">{{errors[Station.passportKeys.streetHouse]}}</div>
						</div>
					</div>
					<div class="row form-row">
						<div class="form-group col-auto my-1 input-min-width">
							<label>
								{{ Station.settingNames[Station.passportKeys.pipelineKm] }}
							</label>
							<Field
								:name="Station.passportKeys.pipelineKm"
								type="text"
								class="form-control form-control-sm"
								:class="{ 'is-invalid': errors[Station.passportKeys.pipelineKm] }"
							/>
							<div class="invalid-feedback">{{errors[Station.passportKeys.pipelineKm]}}</div>
						</div>
					</div>
				</div>
			</template>
		</drop-down-spoiler>

		<!-- Technical data -->
		<a id="technical_data" />
		<drop-down-spoiler v-model:show="spoilersShow['technical_data']" :sticky-title="true">
			<template v-slot:title>
				<h6 class="fs-5 fw-bold m-0">Технічні відомості УКЗ</h6>
			</template>
			<template v-slot:body>
				<div :class="{
					'px-3': isDesktop,
					'px-1': isMobile,
				}">
					<div class="row form-row">
						<div class="form-group col-auto my-1 input-min-width">
							<label>
								{{ Station.settingNames[Station.passportKeys.stationType] }}
							</label>
							<Field
								:name="Station.passportKeys.stationType"
								as="select"
								class="form-control form-control-sm"
								:class="{ 'is-invalid': errors[Station.passportKeys.stationType] }"
							>
								<option value="" disabled>Натисніть щоб обрати</option>
								<option
									v-for="type in Object.values(Station.types)"
									:key="type.code"
									:value="type.code"
								>
									{{ type.title }}
								</option>
							</Field>
							<div class="invalid-feedback">{{errors[Station.passportKeys.stationType]}}</div>
						</div>
					</div>

					<div class="row form-row">
						<div class="form-group col-auto my-1 input-min-width">
							<label>
								{{ Station.settingNames[Station.passportKeys.brandModel] }}
							</label>
							<Field
								:name="Station.passportKeys.brandModel"
								type="text"
								class="form-control form-control-sm"
								:class="{ 'is-invalid': errors[Station.passportKeys.brandModel] }"
							/>
							<div class="invalid-feedback">{{errors[Station.passportKeys.brandModel]}}</div>
						</div>
					</div>
					<div class="row form-row">
						<div class="form-group col-auto my-1 input-min-width">
							<label>
								{{ Station.settingNames[Station.passportKeys.manufacturer] }}
							</label>
							<Field
								:name="Station.passportKeys.manufacturer"
								type="text"
								class="form-control form-control-sm"
								:class="{ 'is-invalid': errors[Station.passportKeys.manufacturer] }"
							/>
							<div class="invalid-feedback">{{ errors[Station.passportKeys.manufacturer] }}</div>
						</div>
					</div>
					<div class="row form-row">
						<div class="form-group col-auto my-1 input-min-width">
							<label>
								{{ Station.settingNames[Station.passportKeys.serialNumber] }}
							</label>
							<Field
								:name="Station.passportKeys.serialNumber"
								type="text"
								class="form-control form-control-sm"
								:class="{ 'is-invalid': errors[Station.passportKeys.serialNumber] }"
							/>
							<div class="invalid-feedback">
								{{ errors[Station.passportKeys.serialNumber] }}
							</div>
						</div>
					</div>
					<div class="row form-row">
						<div class="form-group col-auto my-1 input-min-width">
							<label>
								{{ Station.settingNames[Station.passportKeys.produceDate] }}
							</label>
							<Field
								:name="Station.passportKeys.produceDate"
								type="date"
								class="form-control form-control-sm"
								:class="{ 'is-invalid': errors[Station.passportKeys.produceDate] }"
								@change.stop="onChangeDate(Station.passportKeys.produceDate, $event.target.value)"
							/>
							<div class="invalid-feedback">
								{{ errors[Station.passportKeys.produceDate] }}
							</div>
						</div>
					</div>
					<div class="row form-row">
						<div class="form-group col-auto my-1 input-min-width">
							<label>
								{{ Station.settingNames[Station.passportKeys.protectiveShelterType] }}
							</label>
							<Field
								:name="Station.passportKeys.protectiveShelterType"
								type="text"
								class="form-control form-control-sm"
								:class="{ 'is-invalid': errors[Station.passportKeys.protectiveShelterType] }"
							/>
							<div class="invalid-feedback">
								{{ errors[Station.passportKeys.protectiveShelterType] }}
							</div>
						</div>
					</div>
					<div class="row form-row">
						<label class="row text-start mt-2 mx-0 my-auto">
							{{ Station.settingNames[Station.passportKeys.referenceElectrodePresent] }}
						</label>
						<div class="row mb-2 mt-0 ms-0 btn-group form-group input-min-width" style="width: 15rem;" role="group">
							<Field
								type="radio"
								class="btn-check"
								:class="{ 'is-invalid': errors[Station.passportKeys.referenceElectrodePresent] }"
								:name="Station.passportKeys.referenceElectrodePresent"
								:value="true"
								id="electrode-yes"
							/>
							<label class="col-6 mt-0 btn btn-sm btn-outline-secondary" for="electrode-yes">так</label>

							<Field
								type="radio"
								class="btn-check"
								:class="{ 'is-invalid': errors[Station.passportKeys.referenceElectrodePresent] }"
								:name="Station.passportKeys.referenceElectrodePresent"
								:value="false"
								id="electrode-no"
							/>
							<label class="col-6 mt-0 btn btn-sm btn-outline-secondary" for="electrode-no">ні</label>
						</div>
						<div class="invalid-feedback">
							{{ errors[Station.passportKeys.referenceElectrodePresent] }}
						</div>
					</div>
					<div class="row form-row">
						<label class="row text-start mt-2 mx-0 my-auto">
							{{ Station.settingNames[Station.passportKeys.commonProtectionDevicesPresent] }}
						</label>
						<div class="row mb-2 mt-0 ms-0 btn-group form-group input-min-width" style="width: 15rem;" role="group">
							<Field
								type="radio"
								class="btn-check"
								:class="{ 'is-invalid': errors[Station.passportKeys.commonProtectionDevicesPresent] }"
								:name="Station.passportKeys.commonProtectionDevicesPresent"
								:value="true"
								id="common_protection_devices-yes"
							/>
							<label class="col-6 mt-0 btn btn-sm btn-outline-secondary" for="common_protection_devices-yes">так</label>

							<Field
								type="radio"
								class="btn-check"
								:class="{ 'is-invalid': errors[Station.passportKeys.commonProtectionDevicesPresent] }"
								:name="Station.passportKeys.commonProtectionDevicesPresent"
								:value="false"
								id="common_protection_devices-no"
							/>
							<label class="col-6 mt-0 btn btn-sm btn-outline-secondary" for="common_protection_devices-no">ні</label>
						</div>
						<div class="invalid-feedback">
							{{ errors[Station.passportKeys.commonProtectionDevicesPresent] }}
						</div>
					</div>
					<div class="row form-row">
						<div class="form-group col-auto my-1 input-min-width">
							<label>
								{{ Station.settingNames[Station.passportKeys.commonProtectionDevicesType] }}
							</label>
							<Field
								:name="Station.passportKeys.commonProtectionDevicesType"
								type="text"
								class="form-control form-control-sm"
								:class="{ 'is-invalid': errors[Station.passportKeys.commonProtectionDevicesType] }"
							/>
							<div class="invalid-feedback">
								{{ errors[Station.passportKeys.commonProtectionDevicesType] }}
							</div>
						</div>
					</div>
					<div class="row form-row">
						<div class="form-group col-auto my-1 input-min-width">
							<label>
								{{ Station.settingNames[Station.passportKeys.powerCable] }}
							</label>
							<Field
								:name="Station.passportKeys.powerCable"
								type="text"
								class="form-control form-control-sm"
								:class="{ 'is-invalid': errors[Station.passportKeys.powerCable] }"
							/>
							<div class="invalid-feedback">
								{{ errors[Station.passportKeys.powerCable] }}
							</div>
						</div>
					</div>
					<div class="row form-row">
						<div class="form-group col-auto my-1 input-min-width">
							<label>
								{{ Station.settingNames[Station.passportKeys.anodeCable] }}
							</label>
							<Field
								:name="Station.passportKeys.anodeCable"
								type="text"
								class="form-control form-control-sm"
								:class="{ 'is-invalid': errors[Station.passportKeys.anodeCable] }"
							/>
							<div class="invalid-feedback">
								{{ errors[Station.passportKeys.anodeCable] }}
							</div>
						</div>
					</div>
					<div class="row form-row">
						<div class="form-group col-auto my-1 input-min-width">
							<label>
								{{ Station.settingNames[Station.passportKeys.pipeCable] }}
							</label>
							<Field
								:name="Station.passportKeys.pipeCable"
								type="text"
								class="form-control form-control-sm"
								:class="{ 'is-invalid': errors[Station.passportKeys.pipeCable] }"
							/>
							<div class="invalid-feedback">
								{{ errors[Station.passportKeys.pipeCable] }}
							</div>
						</div>
					</div>
					<div class="row form-row">
						<div class="form-group col-auto my-1 input-min-width">
							<label>
								{{ Station.settingNames[Station.passportKeys.anodeCroundingCableType] }}
							</label>
							<Field
								:name="Station.passportKeys.anodeCroundingCableType"
								type="text"
								class="form-control form-control-sm"
								:class="{ 'is-invalid': errors[Station.passportKeys.anodeCroundingCableType] }"
							/>
							<div class="invalid-feedback">{{errors[Station.passportKeys.anodeCroundingCableType]}}</div>
						</div>
					</div>
					<div class="row form-row">
						<div class="form-group col-auto my-1 input-min-width">
							<label>
								{{ Station.settingNames[Station.passportKeys.electrodesCount] }}
							</label>
							<Field
								:name="Station.passportKeys.electrodesCount"
								type="number"
								class="form-control form-control-sm no-arrow"
								:class="{ 'is-invalid': errors[Station.passportKeys.electrodesCount] }"
							/>
							<div class="invalid-feedback">
								{{ errors[Station.passportKeys.electrodesCount] }}
							</div>
						</div>
					</div>
					<div class="row form-row">
						<div class="form-group col-auto my-1 input-min-width">
							<label>
								{{ Station.settingNames[Station.passportKeys.protectionMethod] }}
							</label>
							<Field
								:name="Station.passportKeys.protectionMethod"
								as="select"
								class="form-control form-control-sm"
								:class="{ 'is-invalid': errors[Station.passportKeys.protectionMethod] }"
								:enabled = "true"
							>
								<option value="" disabled>Натисніть щоб обрати</option>
								<option
									v-for="method in Object.values(Station.protectionMethods)"
									:key="method.key"
									:value="method.key"
								>
									{{ method.title }}
								</option>
							</Field>
							<div class="invalid-feedback">
								{{ errors[Station.passportKeys.protectionMethod] }}
							</div>
						</div>
					</div>
				</div>
			</template>
		</drop-down-spoiler>

		<!-- Electric meter -->
		<a id="electric_meter" />
		<drop-down-spoiler v-model:show="spoilersShow['electric_meter']" :sticky-title="true">
			<template v-slot:title>
				<div class="row row-cols-2">
					<div class="col-auto text-nowrap d-flex">
						<unit-icon unitType="power_meter" />&nbsp;
						<h6 class="fs-5 fw-bold m-0">{{ Station.unitNames[Station.passportKeys.electricMeter.self] }}</h6>
					</div>
				</div>
			</template>
			<template v-slot:body>
				<div :class="{
					'px-3': isDesktop,
					'px-1': isMobile,
				}">
					<div class="row form-row">
						<div class="form-group col-auto my-1 input-min-width">
							<label>
								{{ ElectricMeter.settingNames[ElectricMeter.passportKeys.type] }}
							</label>
							<Field
								:name="`${ElectricMeter.passportKeys.self}.${ElectricMeter.passportKeys.type}`"
								as="select"
								class="form-control form-control-sm"
								:class="{ 'is-invalid': errors[ElectricMeter.passportKeys.self + '.' + ElectricMeter.passportKeys.type] }"
								:disabled="true"
							>
								<option value="" disabled>Не визначено</option>
								<option
									v-for="version in Object.values(ElectricMeter.versions)"
									:key="version"
									:value="version"
								>
									{{ ElectricMeter.versionNames[version] }}
								</option>
							</Field>
							<div class="invalid-feedback">
								{{ errors[ElectricMeter.passportKeys.self + '.' + ElectricMeter.passportKeys.type]}}
							</div>
						</div>
					</div>
					<div class="row form-row">
						<div class="form-group col-auto my-1 input-min-width">
							<label>
								{{ ElectricMeter.settingNames[ElectricMeter.passportKeys.manufacturerId] }}
							</label>
							<Field
								:name="`${ElectricMeter.passportKeys.self}.${ElectricMeter.passportKeys.manufacturerId}`"
								type="text"
								class="form-control form-control-sm"
								:class="{ 'is-invalid': errors[ElectricMeter.passportKeys.self + '.' + ElectricMeter.passportKeys.manufacturerId] }"
								:disabled="true"
							/>
							<div class="invalid-feedback">
								{{ errors[ElectricMeter.passportKeys.self + '.' + ElectricMeter.passportKeys.manufacturerId] }}
							</div>
						</div>
					</div>
					<div class="row form-row">
						<div class="form-group col-auto my-1 input-min-width">
							<label>
								{{ ElectricMeter.settingNames[ElectricMeter.passportKeys.verificationDate] }}
							</label>
							<Field
								:name="`${ElectricMeter.passportKeys.self}.${ElectricMeter.passportKeys.verificationDate}`"
								type="date"
								class="form-control form-control-sm"
								:class="{ 'is-invalid': errors[ElectricMeter.passportKeys.self + '.' + ElectricMeter.passportKeys.verificationDate] }"
								@change.stop="onChangeDate(ElectricMeter.passportKeys.verificationDate, $event.target.value)"
							/>
							<div class="invalid-feedback">
								{{ errors[ElectricMeter.passportKeys.self + '.' + ElectricMeter.passportKeys.verificationDate] }}
							</div>
						</div>
					</div>
				</div>
			</template>
		</drop-down-spoiler>

		<!-- Building passport -->
		<a id="building_passport" />
		<drop-down-spoiler v-model:show="spoilersShow['building_passport']" :sticky-title="true">
			<template v-slot:title>
				<h6 class="fs-5 fw-bold m-0">Будівельний паспорт</h6>
			</template>
			<template v-slot:body>
				<div :class="{
					'px-3': isDesktop,
					'px-1': isMobile,
				}">
					<div class="row form-row">
						<div class="form-group col-auto my-1 input-min-width">
							<label>
								{{ Station.settingNames[Station.passportKeys.installationDate] }}
							</label>
							<Field
								:name="Station.passportKeys.installationDate"
								type="date"
								class="form-control form-control-sm"
								:class="{ 'is-invalid': errors[Station.passportKeys.installationDate] }"
								@change.stop="onChangeDate(Station.passportKeys.installationDate, $event.target.value)"
							/>
							<div class="invalid-feedback">
								{{ errors[Station.passportKeys.installationDate] }}
							</div>
						</div>
					</div>
					<div class="row form-row">
						<div class="form-group col-auto my-1 input-min-width">
							<label>
								{{ Station.settingNames[Station.passportKeys.overhaulDate] }}
							</label>
							<Field
								:name="Station.passportKeys.overhaulDate"
								type="date"
								class="form-control form-control-sm"
								:class="{ 'is-invalid': errors[Station.passportKeys.overhaulDate] }"
								@change.stop="onChangeDate(Station.passportKeys.overhaulDate, $event.target.value)"
							/>
							<div class="invalid-feedback">
								{{ errors[Station.passportKeys.overhaulDate] }}
							</div>
						</div>
					</div>
					<div class="row form-row">
						<div class="form-group col-auto my-1 input-min-width">
							<label>
								{{ Station.settingNames[Station.passportKeys.projectOrganization] }}
							</label>
							<Field
								:name="Station.passportKeys.projectOrganization"
								type="text"
								class="form-control form-control-sm"
								:class="{ 'is-invalid': errors[Station.passportKeys.projectOrganization] }"
							/>
							<div class="invalid-feedback">
								{{ errors[Station.passportKeys.projectOrganization] }}
							</div>
						</div>
					</div>
					<div class="row form-row">
						<div class="form-group col-auto my-1 input-min-width">
							<label>
								{{ Station.settingNames[Station.passportKeys.installationOrganization] }}
							</label>
							<Field
								:name="Station.passportKeys.installationOrganization"
								type="text"
								class="form-control form-control-sm"
								:class="{ 'is-invalid': errors[Station.passportKeys.installationOrganization] }"
							/>
							<div class="invalid-feedback">
								{{ errors[Station.passportKeys.installationOrganization] }}
							</div>
						</div>
					</div>
					<div class="row form-row">
						<div class="form-group col-auto my-1 input-min-width">
							<label>
								{{ Station.settingNames[Station.passportKeys.landUser] }}
							</label>
							<Field
								:name="Station.passportKeys.landUser"
								type="text"
								class="form-control form-control-sm"
								:class="{ 'is-invalid': errors[Station.passportKeys.landUser] }"
							/>
							<div class="invalid-feedback">
								{{ errors[Station.passportKeys.landUser] }}
							</div>
						</div>
					</div>
					<div class="row form-row">
						<div class="form-group col-auto my-1 input-min-width">
							<label>
								{{ Station.settingNames[Station.passportKeys.equipmentNumber] }}
							</label>
							<Field
								:name="Station.passportKeys.equipmentNumber"
								type="number"
								class="form-control form-control-sm no-arrow"
								:class="{ 'is-invalid': errors[Station.passportKeys.equipmentNumber] }"
							/>
							<div class="invalid-feedback">
								{{ errors[Station.passportKeys.equipmentNumber] }}
							</div>
						</div>
					</div>
				</div>
			</template>
		</drop-down-spoiler>

		<a id="related_stations" />
		<div :class="{
			'px-3': isDesktop,
			'px-1': isMobile,
		}">
			<div class="row form-row my-1">
				<div class="form-group col-auto input-min-width mt-1">
					<aside class="m-0 mt-3">
						<h6 class="fs-5 fw-bold m-0">{{ Station.settingNames[Station.passportKeys.relatedStations] }}</h6>
					</aside>
					<!-- Separate Field needed for normal load actual related stations to form -->
					<Field
						:name="Station.passportKeys.relatedStations"
						type="text"
						class="form-control form-control-sm"
						:class="{ 'is-invalid': errors[Station.passportKeys.relatedStations] }"
						hidden
					/>
					<!-- Separete Field end -->
					<FieldArray
						:name="Station.passportKeys.relatedStations"
						v-slot="{ fields, push, remove }"
					>
						<aside v-if="fields.length == 0" class="my-1 text-body-secondary fst-italic">
							Список пустий.
						</aside>
						<fieldset
							class="py-0 my-0"
							v-for="(field, idx) in fields"
							:key="field.key"
						>
							<div class="row align-items-center">
								<div class="col-10 pe-1 my-2">
									{{ stationsStore.station(field.value)?.name ?? 'Станцію було видалено' }}
								</div>
								<!-- Delete item button -->
								<div class="col-1 mx-1 px-0">
									<button
										type="button"
										class="btn btn-sm btn-outline-dark p-1 mx-1"
										style="line-height: 14px;"
										@click="remove(idx)"
									>
										<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-trash3" viewBox="0 0 16 16">
											<path d="M6.5 1h3a.5.5 0 0 1 .5.5v1H6v-1a.5.5 0 0 1 .5-.5ZM11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3A1.5 1.5 0 0 0 5 1.5v1H2.506a.58.58 0 0 0-.01 0H1.5a.5.5 0 0 0 0 1h.538l.853 10.66A2 2 0 0 0 4.885 16h6.23a2 2 0 0 0 1.994-1.84l.853-10.66h.538a.5.5 0 0 0 0-1h-.995a.59.59 0 0 0-.01 0H11Zm1.958 1-.846 10.58a1 1 0 0 1-.997.92h-6.23a1 1 0 0 1-.997-.92L3.042 3.5h9.916Zm-7.487 1a.5.5 0 0 1 .528.47l.5 8.5a.5.5 0 0 1-.998.06L5 5.03a.5.5 0 0 1 .47-.53Zm5.058 0a.5.5 0 0 1 .47.53l-.5 8.5a.5.5 0 1 1-.998-.06l.5-8.5a.5.5 0 0 1 .528-.47ZM8 4.5a.5.5 0 0 1 .5.5v8.5a.5.5 0 0 1-1 0V5a.5.5 0 0 1 .5-.5Z"/>
										</svg>
									</button>
								</div>
							</div> 
						</fieldset>

						<!-- Add related station button -->
						<div class="row align-items-center">
							<div class="col-8 pe-1 my-2">
								<Field
									name="pushedStation"
									as="select"
									class="form-control form-control-sm"
								>
									<option value="" disabled selected>Оберіть станцію</option>
									<option
										v-for="station in stations"
										:key="station.id"
										:value="station.id"
									>
										{{ station.title }}
									</option>
								</Field>
							</div>
							<div class="col-3 ms-4 px-0">
								<button
									type="button"
									class="btn btn-sm btn-outline-dark p-1 pe-2"
									style="line-height: 14px;"
									@click="push(values.pushedStation);"
									:disabled="
										values.pushedStation == undefined
										|| values.pushedStation == ''
										|| values[Station.passportKeys.relatedStations]?.includes(values.pushedStation)
									"
								>
									<svg
										xmlns="http://www.w3.org/2000/svg"
										width="16"
										height="16"
										fill="currentColor"
										class=""
										viewBox="0 0 16 16"
										style="width: 1em; height: 1em;"
									>
										<path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"/>
									</svg>
									Додати
								</button>
							</div>
						</div>
					</FieldArray>
				</div>
			</div>
			
			<div class="row form-row">
				&nbsp;
			</div>
			
			<div class="row form-row">
				<label class="row text-start mt-2 mx-0 my-auto">
					{{ Station.settingNames[Station.passportKeys.isActive] }}
				</label>
				<div class="row mb-2 mt-0 ms-0 btn-group form-group input-min-width" style="width: 15rem;" role="group">
					<Field
						type="radio"
						class="btn-check"
						:class="{ 'is-invalid': errors[Station.passportKeys.isActive] }"
						:name="Station.passportKeys.isActive"
						:value="true"
						id="is_active-yes"
					/>
					<label class="col-6 mt-0 btn btn-sm btn-outline-secondary" for="is_active-yes">так</label>

					<Field
						type="radio"
						class="btn-check"
						:class="{ 'is-invalid': errors[Station.passportKeys.isActive] }"
						:name="Station.passportKeys.isActive"
						:value="false"
						id="is_active-no"
					/>
					<label class="col-6 mt-0 btn btn-sm btn-outline-secondary" for="is_active-no">ні</label>
				</div>
				<div class="invalid-feedback">{{errors[Station.passportKeys.isActive]}}</div>
			</div>
			<div class="row form-row">
				<div
					class="form-group col-auto my-1 input-min-width"
					:style="{ width: formValues?.notes ? formValues?.notes?.length + 'ch' : '15rem' }"
				>
					<label>
						{{ Station.settingNames[Station.passportKeys.notes] }}
					</label>
					<Field
						:name="Station.passportKeys.notes"
						as="textarea"
						type="text"
						class="form-control form-control-sm"
						:class="{ 'is-invalid': errors[Station.passportKeys.notes] }"
					/>
					<div class="invalid-feedback">
						{{ errors[Station.passportKeys.notes] }}
					</div>
				</div>
			</div>

			<div v-if="accountStore.account.role === 'admin'"
				class="row form-row my-3 form-text input-min-width"
			>
				<div class="row">
					<div class="col-auto me-auto">
						station ID
					</div>
					<div class="col-auto ms-auto">
						{{ station.passport.get(Station.passportKeys.id) }}
					</div>
				</div>
				<div class="row">
					<div class="col-auto me-auto">
						{{ Station.passportKeys.metricsId }}
					</div>
					<div class="col-auto ms-auto">
						{{ station.passport.get(Station.passportKeys.metricsId) }}
					</div>
				</div>
			</div>

			<!-- <pre>errors {{ errors }} </pre>
			<pre>meta {{ meta }} </pre>
			<pre>values {{ values }} </pre> -->
		</div>

		<div class="row form-row my-1">
			<span class="form-text fst-italic">
				<p
					class="fs-5 text-center"
					:class="{'text-success': executionOk, 'text-danger': !executionOk}"
				>
					&nbsp;{{ executionResultMessage }}
				</p>
			</span>
		</div>

		<div
			class="row form-row m-0 mt-1 mb-3 p-0"
			:class="{
				'px-3': isDesktop,
				'px-1': isMobile,
			}"
		>
			<div class="form-group col-auto m-0 p-0 me-auto">
				<!-- &nbsp; -->
				<button
					type="button"
					class="btn btn-danger btn-sm"
					@click="showConfirmation=true"
					:disabled="!accountStore.permissions.includes('manage_stations')"
				>
					Видалити
				</button>
				<confirmation-popup
					v-model:show="showConfirmation"
					@confirm="deleteStation();"
				>
					<template v-slot:body>
						<p>
							Ви дійсно хочете <b>видалити</b> станцію?<br>
							Після підтвердження <b class="text-danger">всі</b> дані, налаштування та паспорт станціі будут видалені.
						</p>
					</template>
				</confirmation-popup>
			</div>
			
			<div class="form-group col-auto ms-auto me-2">
				<button
					type="reset"
					class="btn btn-secondary btn-sm mx-2"
				>
					Відмінити
				</button>
			</div>
			<div class="form-group col-auto m-0 p-0">
				<button
					type="submit"
					class="btn btn-primary btn-sm"
					:disabled="!meta.valid"
				>
					Зберегти
				</button>
			</div>
		</div>
	</fieldset>
	</Form>
</div>
</div>
</template>


<script>
import ConfirmationPopup from '@/components/ConfirmationPopup.vue'
import DropDownSpoiler from '@/components/DropDownSpoiler.vue'
import UnitIcon from '@/components/Icons/UnitIcon.vue'
import MenuIcon from '@/components/Icons/MenuIcon.vue'
import { useSizeBreakpoint } from '@/composables/size-breakpoint.js'
import { useAccontStore } from '@/stores/AccountStore'
import { useStationsStore } from '@/stores/StationsStore'
// Validation
// https://jasonwatmore.com/post/2020/10/01/vue-3-veevalidate-form-validation-example
import { Form, Field, FieldArray } from 'vee-validate'
// https://github.com/jquense/yup/tree/a58f02d2f6164c46a9757a818eebac582f7a441c#boolean
import * as Yup from 'yup';
import { PassportSchema } from "@/models/objectPrototype.js"
import { Station } from '@/models/station.js'
import { ElectricMeter } from '@/models/electricMeter.js'
import {
	FIELD_NAMES,
	RESOURCE_NAMES,
	readResources,
	parseTimeDate, convertToApiTimeDate,
	deleteResourceItem
} from '@/api/api.js'
import { dateTimeToDateString } from '@/helpers.js'


export default {
	waitAfterDeleteSuccessMs: 3000,
	requiredPromt: 'Обов\'язкове поле',
	overMinLenPromt: 'Довжина меньше мінімальної',
	overMaxLenPromt: 'Довжина більше максимальної',
	overMinPromt: 'Значення меньше мінімального',
	overMaxPromt: 'Значення більше максимального',
	onlyIntegersPromt: 'Має бути цілим числом',
	onlyPositivePromt: 'Має бути натуральним числом',
	wrongInputPromt: 'Не відповідає вимогам',

	components: {
		ConfirmationPopup,
		Form,
        Field,
		FieldArray,
		DropDownSpoiler,
		UnitIcon,
		MenuIcon,
	},

	props: {
		stationId: {
			type: String,
			require: true,
		},
	},

	setup() {
		const { isDesktop, isMobile } = useSizeBreakpoint()
		const accountStore = useAccontStore()
		const stationsStore = useStationsStore()

		return {
			isDesktop,
			isMobile,
			accountStore,
			stationsStore,
			Station,
			ElectricMeter,
		}
	},

	data() {
		const schema = PassportSchema.shape({
			[Station.passportKeys.techId]: Yup.string()
				.typeError(this.$options.wrongInputPromt)
				.min(1, this.$options.overMinLenPromt)
				.transform((value) => (value ? value : null)) // if empty string
				.max(20, this.$options.overMaxLenPromt),
			[Station.passportKeys.stationType]: Yup.string()
				.typeError(this.$options.wrongInputPromt),
			[Station.passportKeys.protectiveShelterType]: Yup.string()
				.typeError(this.$options.wrongInputPromt)
				.min(1, this.$options.overMinLenPromt)
				.transform((value) => (value ? value : null)) // if empty string
				.max(20, this.$options.overMaxLenPromt),
			[ElectricMeter.passportKeys.self]:	Yup.object().shape({
				[ElectricMeter.passportKeys.type]: Yup.string()
					.typeError(this.$options.wrongInputPromt)
					.min(3, this.$options.overMinLenPromt)
					.transform((value) => (value ? value : null)) // if empty string
					.max(64, this.$options.overMaxLenPromt),
				[ElectricMeter.passportKeys.manufacturerId]: Yup.string()
					.typeError(this.$options.wrongInputPromt)
					.min(1, this.$options.overMinLenPromt)
					.transform((value) => (value ? value : null)) // if empty string
					.max(64, this.$options.overMaxLenPromt),
				[ElectricMeter.passportKeys.verificationDate]: Yup.date()
					.nullable()
					.default(undefined)
					.typeError(this.$options.wrongInputPromt)
					.min(new Date(1900, 0, 1)),
			}),
			[Station.passportKeys.powerCable]: Yup.string()
				.typeError(this.$options.wrongInputPromt)
				.min(1, this.$options.overMinLenPromt)
				.transform((value) => (value ? value : null)) // if empty string
				.max(20, this.$options.overMaxLenPromt),
			[Station.passportKeys.electrodesCount]: Yup.number()
				.typeError(this.$options.wrongInputPromt)
				.integer(this.$options.onlyIntegersPromt)
				.positive(this.$options.onlyPositivePromt)
				.min(0, this.$options.overMinPromt)
				.max(200, this.$options.overMaxPromt),
			[Station.passportKeys.overhaulDate]: Yup.date()
				.nullable()
				.default(undefined)
				.typeError(this.$options.wrongInputPromt)
				.min(new Date(1900, 0, 1)),
			[Station.passportKeys.protectionMethod]: Yup.string()
				.typeError(this.$options.wrongInputPromt),
		})

		return {
			// Form
			schema,
			//
			organizations: [],
			//
			// !key exactly as is!
			spoilersShow: {
				'common_settings': true,
				'organization': false,
				'location': false,
				'technical_data': false,
				'electric_meter': false,
				'building_passport': false,
				'related_stations': false,
			},
			//  
			executionResultMessage: '',
			executionOk: null,
			//
			showConfirmation: false,
		}
	},

	async created() {
		// Read organization depend on user organization role
		if (this.accountStore.isPrimary) {
			// Read all organizations
			this.organizations = await readResources(
				RESOURCE_NAMES.organizations,
				this.accountStore.userLogin,
				this.accountStore.userPassword,
			)
		} else {
			// Read only station organization
			//console.log("organization", this.accountStore.organization)
			this.organizations = [this.accountStore.organization,]
		}
	},

	mounted() {
	},

	methods: {
		// Delete date field if dat is cleared by button "Clear"
		onChangeDate(key, newDate) {
			if (newDate == "") {
				this.$refs.passportForm.setFieldValue(key, null)
			}
		},

		onCancel() {},

		onSubmit(values/*, meta*/) {
			//console.log("values", values)
			//console.log("meta", meta)
			
			this.executionOk = null
			this.executionResultMessage = ''

			values[Station.passportKeys.updateBy] = this.accountStore.userId

			// Delete system fields
			delete values[FIELD_NAMES.createTime]
			delete values[FIELD_NAMES.updateTime]
			delete values[FIELD_NAMES.etag]
			delete values['_items']
			delete values['_status']
			delete values[FIELD_NAMES.latestVersion]
			delete values[FIELD_NAMES.links]
			delete values[FIELD_NAMES.version]
			delete values[Station.passportKeys.department]
			delete values[Station.passportKeys.org]
			delete values[Station.passportKeys.orgName]
			delete values[Station.passportKeys.sector]
			delete values[Station.passportKeys.breaker.self]

			delete values[Station.passportKeys.location]
			delete values["pushedStation"]

			// Enumerate all schema
			for (const [key, value] of Object.entries(this.schema.fields)) {
				// Translate all date to API datetime
				if (value.type == 'date' && values[key]) {
					values[key] = convertToApiTimeDate(values[key])
				}

				// Delete all empty strings
				if (value.type == 'string' && values[key]?.length == 0) {
					delete values[key]
				}

				// Delete all unefined fields
				if (values[key] === undefined) {
					delete values[key]
				}
			}

			// Enumerate electric meter schema
			for (const [key, value] of Object.entries(this.schema.fields[ElectricMeter.passportKeys.self].fields)) {
				// Translate all date to API datetime
				if (value.type == 'date' && values[ElectricMeter.passportKeys.self][key]) {
					values[ElectricMeter.passportKeys.self][key] = convertToApiTimeDate(values[ElectricMeter.passportKeys.self][key])
				}

				// Delete all empty strings
				if (value.type == 'string' && values[ElectricMeter.passportKeys.self][key]?.length == 0) {
					delete values[ElectricMeter.passportKeys.self][key]
				}

				// Delete all unefined fields
				if (values[ElectricMeter.passportKeys.self][key] === undefined) {
					delete values[ElectricMeter.passportKeys.self][key]
				}
			}

			// Remove unknown stations from related stations list
			if (values[Station.passportKeys.relatedStations]) {
				values[Station.passportKeys.relatedStations] = values[Station.passportKeys.relatedStations].filter(
					(field) => this.stationsStore.station(field)
				)
			}
			
			this.stationsStore.updatePasport((result) => {
				if (result) {
					//console.log("Passport updates successfuly")
					this.executionOk = true
					this.executionResultMessage = 'Паспорт записано успішно'
				} else {
					//console.error("Passport update error")
					this.executionOk = false
					this.executionResultMessage = 'Помилка запису'
				}
			}, this.stationId, values)		
        },

		deleteStation() {
			this.executionOk = null
			this.executionResultMessage = ''

			// Delete all 

			// Delete station
			deleteResourceItem(
				RESOURCE_NAMES.stationPassports,
				this.accountStore.userLogin,
				this.accountStore.userPassword,
				this.station.passport.get(Station.passportKeys.id),
				this.station.passport.get("_etag")
			).then(() => {
				this.executionResultMessage = 'Станцію видалено.'
				this.executionOk = true
				
				// Go to stations page after timeout
				setTimeout(
					() => {
						this.$router.push({ name: 'stations',})
					},
					this.$options.waitAfterDeleteSuccessMs
				);
			}).catch(err => {
				console.error("on delete station", err)
				this.executionResultMessage = 
					`Станцію не видалено: сервер повернув помилку.`
				this.executionOk = false
			})
		},

		stationOrg() {
			return this.station.passport.get(Station.passportKeys.org)
		},

		departments() {
			const org = this.stationOrg()
			if (!org) {
				return []
			}

			const deps = org.departments
			if (!deps) {
				return []
			}

			return deps
		},

		departmnetSectors(depId) {
			if (!depId) {
				return []
			}

			const dep = this.departments().find(dep => dep.id == depId)

			if (!dep) {
				return []
			}

			return dep.sectors
		},

		pipelines() {
			const org = this.stationOrg()
			if (!org) {
				return []
			}

			const pipelines = org.pipelines
			if (!pipelines) {
				return []
			}

			return pipelines
		},
	},

	computed: {
		station() { return this.stationsStore.station(this.stationId) },

		stations() {
			let stations = new Array()

			this.stationsStore.stationsArray.forEach(station => {
				// If station from array is related to this station
				if (this.formValues[Station.passportKeys.relatedStations]?.includes(station.id)) {
					return
				}
				// If curent station is this station
				if (station.id == this.stationId) {
					return
				}

				stations.push({id: station.id, title: station.name})
			})

			return stations
		},

		formValues() {
			const passport = Object.fromEntries(
				this.stationsStore.station(this.stationId).passport
			)
			
			// Enumerate all schema. Translate all datetime to form date
			for (const [key, value] of Object.entries(this.schema.fields)) {
				if (value.type == 'date' && passport[key]) {
					const timedate = parseTimeDate(passport[key])
					passport[key] = dateTimeToDateString(timedate)
				}
			}
			
			// Enumerate electric meter schema. Translate all datetime to form date
			for (const [key, value] of Object.entries(this.schema.fields[ElectricMeter.passportKeys.self]?.fields)) {
				if (value.type == 'date' && passport[ElectricMeter.passportKeys.self][key]) {
					const timedate = parseTimeDate(passport[ElectricMeter.passportKeys.self][key])
					passport[ElectricMeter.passportKeys.self][key] = dateTimeToDateString(timedate)
				}
			}

			// Apply station passport to form
			return passport
		},
		
		isFormEnabled() {
			return this.accountStore.permissions.includes('manage_stations')
		},
		
		departmentTitle() {
			return this.accountStore.organization.department_title ? 
				this.accountStore.organization.department_title :
				Station.settingNames[Station.passportKeys.departmentId]
		},

		sectorTitle() {
			return this.accountStore.organization.sector_title ?
				this.accountStore.organization.sector_title :
				Station.settingNames[Station.passportKeys.sectorId]
		},
	},
}
</script>


<style src="@/assets/widget.css" scoped></style>
<style scoped>
/* Remove arrows from input Chrome, Safari, Edge, Opera */
input.no-arrow::-webkit-outer-spin-button,
input.no-arrow::-webkit-inner-spin-button {
	-webkit-appearance: none;
	margin: 0;
}

/* Remove arrows from input Firefox */
input[type=number].no-arrow {
	appearance: textfield;
	-moz-appearance: textfield;
}

.input-min-width {
	min-width: 43ch; 
	max-width: 53ch;
	width: 100%;
}
</style>